import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { formatearMoneda } from '../../utils/helpers';
import { FaWallet, FaPlus, FaEdit, FaLock, FaLockOpen, FaFilePdf, FaEye, FaTimes, FaCheck } from 'react-icons/fa';

export default function CajaChicaPage() {
  const [fondos,       setFondos]       = useState([]);
  const [contadores,   setContadores]   = useState([]);
  const [cargando,     setCargando]     = useState(false);
  const [modalFondo,   setModalFondo]   = useState(false);
  const [modalDetalle, setModalDetalle] = useState(null);
  const [editando,     setEditando]     = useState(null);
  const [form, setForm] = useState({
    monto_inicial: '', fecha_apertura: new Date().toISOString().split('T')[0],
    responsable_id: '', observaciones: '',
  });
  const [error,        setError]        = useState('');
  const [filtroFecha1, setFiltroFecha1] = useState('');
  const [filtroFecha2, setFiltroFecha2] = useState('');

  useEffect(() => { cargar(); cargarContadores(); }, []);

  const cargar = async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/caja-chica/fondos');
      setFondos(data.data || []);
    } catch { setFondos([]); }
    finally { setCargando(false); }
  };

  const cargarContadores = async () => {
    try {
      const { data } = await api.get('/usuarios');
      setContadores((data.data || []).filter(u => u.activo));
    } catch { setContadores([]); }
  };

  const handleSubmit = async () => {
    if (!form.monto_inicial || !form.fecha_apertura || !form.responsable_id) {
      setError('Completa todos los campos requeridos.'); return;
    }
    try {
      if (editando) {
        await api.put(`/caja-chica/fondos/${editando}`, form);
      } else {
        await api.post('/caja-chica/fondos', form);
      }
      setModalFondo(false);
      setEditando(null);
      setForm({ monto_inicial: '', fecha_apertura: new Date().toISOString().split('T')[0], responsable_id: '', observaciones: '' });
      setError('');
      cargar();
    } catch (e) {
      setError(e.response?.data?.message || 'Error al guardar.');
    }
  };

  const handleEditar = (fondo) => {
    setEditando(fondo.id);
    setForm({
      monto_inicial:  fondo.monto_inicial,
      fecha_apertura: fondo.fecha_apertura,
      responsable_id: fondo.responsable_id,
      observaciones:  fondo.observaciones || '',
    });
    setModalFondo(true);
  };

  const handleCerrar = async (id) => {
    if (!window.confirm('¿Cerrar esta caja chica?')) return;
    await api.patch(`/caja-chica/fondos/${id}/cerrar`);
    cargar();
  };

  const handleReabrir = async (id) => {
    if (!window.confirm('¿Reabrir esta caja chica?')) return;
    await api.patch(`/caja-chica/fondos/${id}/reabrir`);
    cargar();
  };

  const handlePdf = async (id) => {
    try {
      let url = `/caja-chica/fondos/${id}/reporte-pdf`;
      const params = [];
      if (filtroFecha1) params.push(`fecha_inicio=${filtroFecha1}`);
      if (filtroFecha2) params.push(`fecha_fin=${filtroFecha2}`);
      if (params.length) url += '?' + params.join('&');

      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `caja_chica_${id}.pdf`;
      link.click();
    } catch {
      alert('Error al generar el PDF.');
    }
  };

  const totalFondos    = fondos.reduce((a, f) => a + Number(f.monto_inicial), 0);
  const totalGastado   = fondos.reduce((a, f) => a + Number(f.total_gastado), 0);
  const fondosAbiertos = fondos.filter(f => f.estado === 'abierta').length;

  return (
    <Layout>
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4><FaWallet className="me-2" />Caja Chica</h4>
          <p className="text-muted mb-0">Gestión de fondos y movimientos de caja chica</p>
        </div>
        <button className="btn btn-danger btn-sm" onClick={() => { setModalFondo(true); setEditando(null); setError(''); }}>
          <FaPlus className="me-1" /> Nuevo Fondo
        </button>
      </div>

      {/* KPIs */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Total Fondos',   valor: totalFondos,              color: '#2980B9', esMoneda: true },
          { label: 'Total Gastado',  valor: totalGastado,             color: '#E74C3C', esMoneda: true },
          { label: 'Saldo Global',   valor: totalFondos - totalGastado, color: '#27AE60', esMoneda: true },
          { label: 'Cajas Abiertas', valor: fondosAbiertos,           color: '#8E44AD', esMoneda: false },
        ].map((k, i) => (
          <div className="col-md-3" key={i}>
            <div className="card card-custom h-100">
              <div className="card-body">
                <div className="small text-muted fw-bold mb-1">{k.label}</div>
                <div className="fw-bold fs-5" style={{ color: k.color }}>
                  {k.esMoneda ? formatearMoneda(k.valor) : k.valor}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filtros PDF */}
      <div className="card card-custom mb-4">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label small fw-bold">Filtrar PDF desde</label>
              <input type="date" className="form-control form-control-sm"
                value={filtroFecha1} onChange={e => setFiltroFecha1(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-bold">Hasta</label>
              <input type="date" className="form-control form-control-sm"
                value={filtroFecha2} onChange={e => setFiltroFecha2(e.target.value)} />
            </div>
            <div className="col-md-6">
              <p className="small text-muted mb-0">Los filtros se aplican al descargar el PDF de cada caja.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla fondos */}
      <div className="card card-custom">
        <div className="card-body">
          <h6 className="fw-bold mb-3">Fondos de Caja Chica</h6>
          {cargando ? (
            <div className="text-center py-4"><div className="spinner-border text-danger" /></div>
          ) : fondos.length === 0 ? (
            <div className="text-center py-4 text-muted">No hay fondos registrados.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th>Responsable</th>
                    <th>Apertura</th>
                    <th className="text-end">Fondo</th>
                    <th className="text-end">Gastado</th>
                    <th className="text-end">Saldo</th>
                    <th className="text-center">Movs.</th>
                    <th className="text-center">Estado</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {fondos.map(f => (
                    <tr key={f.id}>
                      <td className="fw-bold">{f.responsable_nombre}</td>
                      <td>{f.fecha_apertura}</td>
                      <td className="text-end text-primary">{formatearMoneda(f.monto_inicial)}</td>
                      <td className="text-end text-danger">{formatearMoneda(f.total_gastado)}</td>
                      <td className="text-end fw-bold" style={{ color: f.saldo_disponible >= 0 ? '#27AE60' : '#E74C3C' }}>
                        {formatearMoneda(f.saldo_disponible)}
                      </td>
                      <td className="text-center">{f.total_movimientos}</td>
                      <td className="text-center">
                        <span className={`badge ${f.estado === 'abierta' ? 'bg-success' : 'bg-secondary'}`}>
                          {f.estado === 'abierta' ? 'Abierta' : 'Cerrada'}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-1 justify-content-center">
                          <button className="btn btn-outline-info btn-sm" title="Ver movimientos"
                            onClick={() => setModalDetalle(f)}>
                            <FaEye />
                          </button>
                          {f.estado === 'abierta' && f.total_movimientos === 0 && (
                            <button className="btn btn-outline-warning btn-sm" title="Editar"
                              onClick={() => handleEditar(f)}>
                              <FaEdit />
                            </button>
                          )}
                          {f.estado === 'abierta' ? (
                            <button className="btn btn-outline-secondary btn-sm" title="Cerrar caja"
                              onClick={() => handleCerrar(f.id)}>
                              <FaLock />
                            </button>
                          ) : (
                            <button className="btn btn-outline-success btn-sm" title="Reabrir caja"
                              onClick={() => handleReabrir(f.id)}>
                              <FaLockOpen />
                            </button>
                          )}
                          <button className="btn btn-outline-danger btn-sm" title="Descargar PDF"
                            onClick={() => handlePdf(f.id)}>
                            <FaFilePdf />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Nuevo/Editar Fondo */}
      {modalFondo && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  <FaWallet className="me-2" />{editando ? 'Editar Fondo' : 'Nuevo Fondo de Caja Chica'}
                </h5>
                <button className="btn-close" onClick={() => { setModalFondo(false); setEditando(null); setError(''); }} />
              </div>
              <div className="modal-body">
                {error && <div className="alert alert-danger py-2">{error}</div>}
                <div className="mb-3">
                  <label className="form-label small fw-bold">Monto Inicial (S/) *</label>
                  <input type="number" className="form-control form-control-sm"
                    value={form.monto_inicial}
                    onChange={e => setForm({ ...form, monto_inicial: e.target.value })}
                    placeholder="0.00" min="0" step="0.01" />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Fecha de Apertura *</label>
                  <input type="date" className="form-control form-control-sm"
                    value={form.fecha_apertura}
                    onChange={e => setForm({ ...form, fecha_apertura: e.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Responsable *</label>
                  <select className="form-select form-select-sm"
                    value={form.responsable_id}
                    onChange={e => setForm({ ...form, responsable_id: e.target.value })}>
                    <option value="">Seleccionar responsable</option>
                    {contadores.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.nombre} {u.apellido} ({u.rol})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Observaciones</label>
                  <textarea className="form-control form-control-sm" rows={3}
                    value={form.observaciones}
                    onChange={e => setForm({ ...form, observaciones: e.target.value })}
                    placeholder="Observaciones opcionales..." />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm"
                  onClick={() => { setModalFondo(false); setEditando(null); setError(''); }}>
                  <FaTimes className="me-1" /> Cancelar
                </button>
                <button className="btn btn-danger btn-sm" onClick={handleSubmit}>
                  <FaCheck className="me-1" /> {editando ? 'Actualizar' : 'Crear Fondo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle Movimientos */}
      {modalDetalle && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  Movimientos — {modalDetalle.responsable_nombre}
                </h5>
                <button className="btn-close" onClick={() => setModalDetalle(null)} />
              </div>
              <div className="modal-body">
                <MovimientosDetalle fondo={modalDetalle} />
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function MovimientosDetalle({ fondo }) {
  const [movimientos, setMovimientos] = useState([]);
  const [resumen,     setResumen]     = useState(null);
  const [cargando,    setCargando]    = useState(true);

  useEffect(() => {
    api.get(`/caja-chica/fondos/${fondo.id}/movimientos`)
      .then(({ data }) => { setMovimientos(data.data || []); setResumen(data.resumen); })
      .catch(() => {})
      .finally(() => setCargando(false));
  }, [fondo.id]);

  if (cargando) return <div className="text-center py-3"><div className="spinner-border text-danger" /></div>;

  return (
    <>
      <div className="row g-2 mb-3">
        {[
          { label: 'Fondo Inicial',    valor: resumen?.monto_inicial,    color: '#2980B9' },
          { label: 'Total Gastado',    valor: resumen?.total_gastado,    color: '#E74C3C' },
          { label: 'Saldo Disponible', valor: resumen?.saldo_disponible, color: '#27AE60' },
        ].map((k, i) => (
          <div className="col-md-4" key={i}>
            <div className="p-2 rounded text-center" style={{ background: '#f8f9fa' }}>
              <div className="small text-muted">{k.label}</div>
              <div className="fw-bold" style={{ color: k.color }}>{formatearMoneda(k.valor)}</div>
            </div>
          </div>
        ))}
      </div>
      {movimientos.length === 0 ? (
        <div className="text-center py-3 text-muted">Sin movimientos registrados.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover">
            <thead className="table-dark">
              <tr>
                <th>Fecha</th><th>Descripción</th><th>Categoría</th>
                <th>Registrado por</th><th className="text-end">Monto</th>
              </tr>
            </thead>
            <tbody>
              {movimientos.map(m => (
                <tr key={m.id}>
                  <td>{m.fecha}</td>
                  <td>{m.descripcion}</td>
                  <td><span className="badge bg-secondary">{m.categoria || '—'}</span></td>
                  <td>{m.usuario_nombre}</td>
                  <td className="text-end text-danger fw-bold">{formatearMoneda(m.monto)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="table-dark">
              <tr>
                <td colSpan="4" className="fw-bold">TOTAL</td>
                <td className="text-end fw-bold">{formatearMoneda(resumen?.total_gastado)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  );
}